import { useEffect, useRef, useCallback, type RefObject } from 'react';
import {
  drawBrushStroke,
  clearCursorLayer,
  drawCursorPreview,
} from '../components/canvas-renderer.js';
import { useEditorStore, BRUSH_SIZE_MIN, BRUSH_SIZE_MAX } from '../../../stores/editor.js';
import type { ViewportRef } from '../components/editor-canvas.js';

interface UseCanvasInteractionOptions {
  cursorCanvasRef: RefObject<HTMLCanvasElement | null>;
  offscreenMaskCtxRef: RefObject<CanvasRenderingContext2D | null>;
  dimensionsRef: RefObject<{ width: number; height: number }>;
  refreshOverlay: () => void;
  viewportRef: RefObject<ViewportRef>;
  onViewportChange: () => void;
}

const BRUSH_STEP = 4;
const BRACKET_STEP = 4;
const ZOOM_SENSITIVITY = 0.002;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4.0;

/**
 * Interpolate points between two positions to avoid gaps at fast movement.
 * Step size = brushSize / 4 per story spec (Bresenham-style).
 */
function interpolatePoints(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  brushSize: number,
): Array<{ x: number; y: number }> {
  const step = Math.max(1, brushSize / 4);
  const dx = x1 - x0;
  const dy = y1 - y0;
  const dist = Math.sqrt(dx * dx + dy * dy);

  if (dist < step) {
    return [{ x: x1, y: y1 }];
  }

  const points: Array<{ x: number; y: number }> = [];
  const count = Math.ceil(dist / step);
  for (let i = 1; i <= count; i++) {
    const t = i / count;
    points.push({ x: x0 + dx * t, y: y0 + dy * t });
  }
  return points;
}

export function useCanvasInteraction({
  cursorCanvasRef,
  offscreenMaskCtxRef,
  dimensionsRef,
  refreshOverlay,
  viewportRef,
  onViewportChange,
}: UseCanvasInteractionOptions): void {
  const isDrawingRef = useRef(false);
  const isPanningRef = useRef(false);
  const activePointerIdRef = useRef<number | null>(null);
  const panPointerIdRef = useRef<number | null>(null);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const panStartRef = useRef<{ x: number; y: number } | null>(null);
  const cursorPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const rafIdRef = useRef<number>(0);
  const spaceHeldRef = useRef(false);

  // Read store values via ref to avoid re-renders in the animation loop
  const toolRef = useRef(useEditorStore.getState().tool);
  const brushSizeRef = useRef(useEditorStore.getState().brushSize);

  // Sync store to refs
  useEffect(() => {
    return useEditorStore.subscribe((state) => {
      toolRef.current = state.tool;
      brushSizeRef.current = state.brushSize;
    });
  }, []);

  const getCanvasCoords = useCallback(
    (e: PointerEvent): { x: number; y: number } | null => {
      const canvas = cursorCanvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const dims = dimensionsRef.current;
      if (dims.width === 0 || dims.height === 0) return null;
      const scaleX = dims.width / rect.width;
      const scaleY = dims.height / rect.height;
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    },
    [cursorCanvasRef, dimensionsRef],
  );

  // Cursor preview via rAF
  const renderCursor = useCallback(() => {
    const canvas = cursorCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    clearCursorLayer(ctx);
    drawCursorPreview(
      ctx,
      cursorPosRef.current.x,
      cursorPosRef.current.y,
      brushSizeRef.current,
      toolRef.current,
    );

    rafIdRef.current = requestAnimationFrame(renderCursor);
  }, [cursorCanvasRef]);

  useEffect(() => {
    const canvas = cursorCanvasRef.current;
    if (!canvas) return;

    const handlePointerDown = (e: PointerEvent) => {
      // Lock: once the result is accepted, the canvas is frozen. Pan and
      // middle-click are disabled too — no interaction at all.
      if (useEditorStore.getState().sessionFinished) return;

      // Middle mouse button: start panning
      if (e.button === 1) {
        e.preventDefault();
        isPanningRef.current = true;
        panPointerIdRef.current = e.pointerId;
        panStartRef.current = { x: e.clientX, y: e.clientY };
        canvas.setPointerCapture(e.pointerId);
        return;
      }

      if (e.button !== 0) return; // only left click from here

      // Space+left-click: pan mode instead of drawing
      if (spaceHeldRef.current) {
        isPanningRef.current = true;
        panPointerIdRef.current = e.pointerId;
        panStartRef.current = { x: e.clientX, y: e.clientY };
        canvas.setPointerCapture(e.pointerId);
        canvas.style.cursor = 'grabbing';
        return;
      }

      if (isDrawingRef.current) return; // ignore secondary touch/pointer

      const coords = getCanvasCoords(e);
      if (!coords) return;

      // Implicit "I'm not done" signal: if the user starts drawing while the
      // result-review overlay is up, dismiss the overlay automatically. The
      // explicit Keep editing button is gone — drawing IS the signal.
      // submissionStatus === 'success' is the only state where the overlay
      // shows actionable buttons; flipping back to 'idle' hides them and
      // re-enables Generate without forcing a separate click.
      if (useEditorStore.getState().submissionStatus === 'success') {
        useEditorStore.getState().dismissReview();
      }

      canvas.setPointerCapture(e.pointerId);
      activePointerIdRef.current = e.pointerId;
      isDrawingRef.current = true;
      lastPointRef.current = coords;

      const maskCtx = offscreenMaskCtxRef.current;
      if (!maskCtx) return;

      // Capture undo snapshot BEFORE the stroke begins (physical pixel coords for HiDPI)
      const dims = dimensionsRef.current;
      const dpr = window.devicePixelRatio || 1;
      if (dims.width > 0 && dims.height > 0) {
        const snapshot = maskCtx.getImageData(0, 0, dims.width * dpr, dims.height * dpr);
        useEditorStore.getState().pushUndo(snapshot);
      }

      const pressure = e.pointerType === 'pen' ? e.pressure : undefined;
      drawBrushStroke(maskCtx, coords.x, coords.y, brushSizeRef.current, toolRef.current, pressure);
      refreshOverlay();
    };

    const handlePointerMove = (e: PointerEvent) => {
      // Lock: freeze all pointer interaction once the result is accepted
      if (useEditorStore.getState().sessionFinished) return;

      // Handle panning movement
      if (isPanningRef.current && e.pointerId === panPointerIdRef.current) {
        const start = panStartRef.current;
        if (!start) return;
        const vp = viewportRef.current;
        vp.offsetX += e.clientX - start.x;
        vp.offsetY += e.clientY - start.y;
        panStartRef.current = { x: e.clientX, y: e.clientY };
        onViewportChange();
        return;
      }

      const coords = getCanvasCoords(e);
      if (!coords) return;

      cursorPosRef.current = coords;

      if (!isDrawingRef.current || e.pointerId !== activePointerIdRef.current) return;

      const maskCtx = offscreenMaskCtxRef.current;
      if (!maskCtx) return;

      const last = lastPointRef.current;
      if (!last) {
        lastPointRef.current = coords;
        return;
      }

      const pressure = e.pointerType === 'pen' ? e.pressure : undefined;
      const points = interpolatePoints(last.x, last.y, coords.x, coords.y, brushSizeRef.current);
      for (const pt of points) {
        drawBrushStroke(maskCtx, pt.x, pt.y, brushSizeRef.current, toolRef.current, pressure);
      }

      lastPointRef.current = coords;
      refreshOverlay();
    };

    const handlePointerUp = (e: PointerEvent) => {
      // Lock: if accept fired mid-stroke (e.g. user pressed Enter while
      // drawing), the stroke must not complete into the store — the canvas
      // is supposed to be frozen. Still release the captured pointer and
      // clear refs so the next iteration starts clean.
      const isLocked = useEditorStore.getState().sessionFinished;

      // End panning
      if (isPanningRef.current && e.pointerId === panPointerIdRef.current) {
        canvas.releasePointerCapture(e.pointerId);
        isPanningRef.current = false;
        panPointerIdRef.current = null;
        panStartRef.current = null;
        // Drop back to 'grab' if Space is still held, otherwise to the
        // brush-preview cursor ('none').
        canvas.style.cursor = spaceHeldRef.current ? 'grab' : 'none';
        return;
      }

      // End drawing — sync mask state to store for undo/redo integrity
      if (isDrawingRef.current && e.pointerId === activePointerIdRef.current) {
        canvas.releasePointerCapture(e.pointerId);
        isDrawingRef.current = false;
        activePointerIdRef.current = null;
        lastPointRef.current = null;

        if (isLocked) return;

        // Sync current mask canvas to store so undo() can push it to redoStack
        const maskCtx = offscreenMaskCtxRef.current;
        const dims = dimensionsRef.current;
        if (maskCtx && dims.width > 0 && dims.height > 0) {
          const dpr = window.devicePixelRatio || 1;
          const currentState = maskCtx.getImageData(0, 0, dims.width * dpr, dims.height * dpr);
          useEditorStore.getState().setMaskData(currentState);
        }
      }
    };

    const handleWheel = (e: WheelEvent) => {
      // Lock: no zoom/pan/brush-size adjustment once the result is accepted
      if (useEditorStore.getState().sessionFinished) return;
      e.preventDefault();
      if (isPanningRef.current) return; // don't compete with an active space-pan

      // Shift+scroll: adjust brush size (power-user shortcut, kept).
      if (e.shiftKey && !e.ctrlKey && !e.metaKey) {
        if (e.deltaY === 0) return;
        const store = useEditorStore.getState();
        const delta = e.deltaY < 0 ? BRUSH_STEP : -BRUSH_STEP;
        const newSize = Math.max(BRUSH_SIZE_MIN, Math.min(BRUSH_SIZE_MAX, store.brushSize + delta));
        store.setBrushSize(newSize);
        return;
      }

      // Pinch-to-zoom on a trackpad fires `wheel` with ctrlKey synthetically
      // set by the browser — same hook as ctrl/cmd+scroll for mouse users.
      // Figma, Tldraw, Excalidraw all key zoom off this signal.
      if (e.ctrlKey || e.metaKey) {
        if (e.deltaY === 0) return;
        const vp = viewportRef.current;
        const oldScale = vp.scale;
        // Clamp the delta so a mouse wheel "click" (deltaY=100) doesn't yank
        // the zoom several steps while a trackpad pinch (deltaY≈1) feels
        // smooth. ±10 matches the de-facto clamp used by canvas tools.
        const clampedDelta = Math.max(-10, Math.min(10, e.deltaY));
        const newScale = Math.max(
          ZOOM_MIN,
          Math.min(ZOOM_MAX, oldScale * (1 - clampedDelta * ZOOM_SENSITIVITY * 10)),
        );

        // Zoom centered on cursor position
        const cx = e.clientX;
        const cy = e.clientY;
        vp.offsetX = cx - (cx - vp.offsetX) * (newScale / oldScale);
        vp.offsetY = cy - (cy - vp.offsetY) * (newScale / oldScale);
        vp.scale = newScale;
        onViewportChange();
        return;
      }

      // Default: two-finger trackpad scroll = pan (Figma idiom). Mouse-wheel
      // users still get this too, but the typical mouse user reaches for
      // Space+drag or middle-click for panning, so this is mostly trackpad
      // ergonomics. Both deltaX and deltaY translate the viewport directly.
      if (e.deltaX === 0 && e.deltaY === 0) return;
      const vp = viewportRef.current;
      vp.offsetX -= e.deltaX;
      vp.offsetY -= e.deltaY;
      onViewportChange();
    };

    // Prevent default on middle-click to avoid browser auto-scroll cursor
    const handleAuxClick = (e: MouseEvent) => {
      if (e.button === 1) {
        e.preventDefault();
      }
    };

    canvas.addEventListener('pointerdown', handlePointerDown);
    canvas.addEventListener('pointermove', handlePointerMove);
    canvas.addEventListener('pointerup', handlePointerUp);
    canvas.addEventListener('pointercancel', handlePointerUp);
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    canvas.addEventListener('auxclick', handleAuxClick);

    // Start cursor render loop
    rafIdRef.current = requestAnimationFrame(renderCursor);

    return () => {
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('pointerup', handlePointerUp);
      canvas.removeEventListener('pointercancel', handlePointerUp);
      canvas.removeEventListener('wheel', handleWheel);
      canvas.removeEventListener('auxclick', handleAuxClick);
      cancelAnimationFrame(rafIdRef.current);
    };
  }, [
    cursorCanvasRef,
    offscreenMaskCtxRef,
    dimensionsRef,
    getCanvasCoords,
    refreshOverlay,
    renderCursor,
    viewportRef,
    onViewportChange,
  ]);

  // Keyboard shortcuts (on window, not canvas)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Lock: ignore brush/tool/undo shortcuts once the result is accepted.
      // The ResultReviewActions component owns Enter/Escape separately.
      if (useEditorStore.getState().sessionFinished) return;

      // Track space key for Space+drag panning
      if (e.code === 'Space' && !e.repeat) {
        // Skip if user is typing in an input or contenteditable element
        const active = document.activeElement as HTMLElement | null;
        const tag = active?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (active?.isContentEditable) return;

        e.preventDefault();
        spaceHeldRef.current = true;
        // Hint that pan-on-click is available now.
        const canvas = cursorCanvasRef.current;
        if (canvas && !isPanningRef.current) {
          canvas.style.cursor = 'grab';
        }
        return;
      }

      // Skip if user is typing in an input or contenteditable element
      const active = document.activeElement as HTMLElement | null;
      const tag = active?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (active?.isContentEditable) return;

      const store = useEditorStore.getState();

      // Undo/redo shortcuts
      if (e.ctrlKey || e.metaKey) {
        const key = e.key.toLowerCase();
        if (key === 'z' && !e.shiftKey) {
          e.preventDefault();
          store.undo();
          return;
        }
        if ((key === 'z' && e.shiftKey) || key === 'y') {
          e.preventDefault();
          store.redo();
          return;
        }
      }

      switch (e.key.toLowerCase()) {
        case 'b':
          store.setTool('brush');
          break;
        case 'e':
          store.setTool('eraser');
          break;
        case '[':
          store.setBrushSize(Math.max(BRUSH_SIZE_MIN, store.brushSize - BRACKET_STEP));
          break;
        case ']':
          store.setBrushSize(Math.min(BRUSH_SIZE_MAX, store.brushSize + BRACKET_STEP));
          break;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        spaceHeldRef.current = false;
        // Restore the brush-preview cursor — but only if we're not
        // mid-pan (releasing Space while still dragging shouldn't suddenly
        // hide the cursor). The pointerup handler will restore on release.
        const canvas = cursorCanvasRef.current;
        if (canvas && !isPanningRef.current) {
          canvas.style.cursor = 'none';
        }
      }
    };

    // Clear space-held state when window loses focus (prevents stuck pan mode after alt-tab)
    const handleBlur = () => {
      spaceHeldRef.current = false;
      const canvas = cursorCanvasRef.current;
      if (canvas && !isPanningRef.current) {
        canvas.style.cursor = 'none';
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, [cursorCanvasRef]);
}
